import React from 'react';

interface ScoreChipProps {
  score: number | null; // 0-10 scale, null for unscored
  modelName?: string;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * ScoreChip - Colored badge showing composite LLM judge score
 * S9: Feature-gated component for displaying model scores
 *
 * Color coding:
 * - Green (>8.0): Excellent
 * - Yellow (6.0-8.0): Good
 * - Gray (<6.0): Needs improvement
 */
export const ScoreChip: React.FC<ScoreChipProps> = ({
  score,
  modelName,
  showLabel = true,
  size = 'md'
}) => {
  // Don't render if score is null (unscored response)
  if (score === null || score === undefined) {
    return null;
  }

  // Determine color based on score
  const getColorClasses = () => {
    if (score >= 8.0) {
      return 'bg-green-100 text-green-800 border-green-200';
    } else if (score >= 6.0) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else {
      return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  // Size classes
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5'
  };

  const colorClasses = getColorClasses();
  const sizeClass = sizeClasses[size];

  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${colorClasses} ${sizeClass}`}
      title={modelName ? `${modelName}: ${score.toFixed(2)}/10` : `Score: ${score.toFixed(2)}/10`}
    >
      {showLabel && (
        <span className="opacity-75">Score:</span>
      )}
      <span className="font-bold">{score.toFixed(2)}</span>
      <span className="opacity-60">/10</span>
    </div>
  );
};
